import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
import useAxiosPrivate from "../hooks/useAxiosPrivate";

//
import { isValidToken, setSession, setCurrentUserSession } from './utils';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
    isInitialized: false,
    isAuthenticated: false,
    user: null,
};

const reducer = (state, action) => {
    if (action.type === 'INITIAL') {
        return {
            isInitialized: true,
            isAuthenticated: action.payload.isAuthenticated,
            user: action.payload.user,
        };
    }
    if (action.type === 'LOGIN') {
        return {
            ...state,
            isAuthenticated: true,
            user: action.payload.user,
        };
    }
    if (action.type === 'REGISTER') {
        return {
            ...state,
            isAuthenticated: true,
            user: action.payload.user,
        };
    }
    if (action.type === 'LOGOUT') {
        return {
            ...state,
            isAuthenticated: false,
            user: null,
        };
    }

    return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
    children: PropTypes.node,
};

export function AuthProvider({ children }) {
    const { instance } = useMsal();
    const [state, dispatch] = useReducer(reducer, initialState);

    const storageAvailable = localStorageAvailable();
    const axiosPrivate = useAxiosPrivate();

    const initialize = useCallback(async () => {
        try {
            const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
            const refreshToken = storageAvailable ? localStorage.getItem('refreshToken') : '';
            const currentUser = storageAvailable ? localStorage.getItem('currentUser') : '';

            if (accessToken && isValidToken(accessToken) && currentUser) {
                setSession(accessToken, refreshToken);

                // const response = await axios.get('/api/account/my-account');

                // const { user } = currentUser;

                dispatch({
                    type: 'INITIAL',
                    payload: {
                        isAuthenticated: true,
                        user: JSON.parse(currentUser),
                    },
                });
            } else {
                dispatch({
                    type: 'INITIAL',
                    payload: {
                        isAuthenticated: false,
                        user: null,
                    },
                });
            }
        } catch (error) {
            console.error(error);
            dispatch({
                type: 'INITIAL',
                payload: {
                    isAuthenticated: false,
                    user: null,
                },
            });
        }
    }, [storageAvailable]);

    useEffect(() => {
        initialize();
    }, [initialize]);

    // LOGIN
    const login = useCallback(async (email, password) => {
        if (process.env.REACT_APP_MSALEnable === 'true') {
            instance.loginPopup({
                scopes: ["user.read"],
            }).then(async (result) => {
                // const user = {
                //     id: 1,
                //     firstName: result.account.name.split(' ')[0],
                //     lastName: result.account.name.split(' ')[1],
                //     email: result.account.username,
                //     isActive: true,
                //     role: { id: 1, name: "Admin" }
                // }
                email=result.account.username;
                const response = await axios.post('/auth/mfalogin', {
                    email                    
                });
                const { token, user, refreshToken } = response.data;
                
                setSession(token, refreshToken);
                setCurrentUserSession(JSON.stringify(user));
                dispatch({
                    type: 'LOGIN',
                    payload: {
                        isAuthenticated: true,
                        user,
                    },
                });
            });
        }
        else {

            const response = await axios.post('/auth/login', {
                email,
                password
            });
            const { token, user, refreshToken } = response.data;

            setSession(token, refreshToken);
            setCurrentUserSession(JSON.stringify(user));
            console.log(JSON.stringify(user));
            dispatch({
                type: 'LOGIN',
                payload: {
                    user,
                },
            });
        }
    }, [instance]);

    // REGISTER
    const register = useCallback(async (email, password, firstName, lastName) => {
        const response = await axios.post('/api/account/register', {
            email,
            password,
            firstName,
            lastName,
        });
        const { accessToken, user } = response.data;

        localStorage.setItem('accessToken', accessToken);

        dispatch({
            type: 'REGISTER',
            payload: {
                user,
            },
        });
    }, []);

    // LOGOUT
    const logout = useCallback(async () => {
        if (process.env.REACT_APP_MSALEnable === 'true') {
            dispatch({
                type: 'LOGOUT',
            });
            instance.logoutPopup().then((res) => {
                setSession(null);
                setCurrentUserSession(null);
            });
        }
        else {


            try {
                const token = localStorage.getItem('accessToken');

                await axiosPrivate.post('/auth/logout', {
                    token
                });
            }
            catch (error) {
                console.log(error);
            }

            setSession(null);
            setCurrentUserSession(null);

            dispatch({
                type: 'LOGOUT',
            });
        }
    }, [axiosPrivate, instance]);

    const memoizedValue = useMemo(
        () => ({
            isInitialized: state.isInitialized,
            isAuthenticated: state.isAuthenticated,
            user: state.user,
            method: 'jwt',
            login,
            register,
            logout,
        }),
        [state.isAuthenticated, state.isInitialized, state.user, login, logout, register]
    );

    return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
